// C8 sub-reason taxonomy (09-business-rule-decisions.md §C8).
//
// "Global default + tenant override" — the backend ships a system seed and may
// extend per tenant. The backend does NOT yet enforce / persist a sub-reason on
// `POST /ord/returns` (the create Joi has no `subReason` field — see the wizard
// report), so this default taxonomy is hardcoded FE-side purely to drive the
// Step-2 dropdown. When the create contract gains a `subReason` field this map
// can be replaced by a settings fetch and the value wired into the payload.
//
// Spec table keys are mapped to the real ReturnReason enum:
//   - spec "BUYER_CHANGED_MIND" → enum CHANGED_MIND
//   - FRAUD_SUSPICION is system-only (buyer cannot pick) → omitted
import { i18n } from "@dehub/theme";
import type { ReturnReason } from "../../../../apis/return";

export interface SubReasonOption {
  value: string;
  /** Label resolved at call time via i18n (buyerApp:subReason.*). */
  labelMn: string;
}

// Per-reason default sub-reason value codes. Reasons absent from this map have
// no sub-reason dropdown (Step 2 simply hides it). Labels come from i18n.
const SUB_REASON_VALUES_BY_REASON: Partial<Record<ReturnReason, string[]>> = {
  DEFECTIVE: ["ELECTRICAL", "MECHANICAL", "SOFTWARE", "COSMETIC", "MANUFACTURING_DEFECT", "OTHER"],
  DELIVERY_DAMAGED: ["PACKAGING_TORN", "ITEM_BROKEN", "WET", "CRUSHED", "OTHER"],
  WRONG_ITEM_DELIVERED: ["WRONG_SKU", "WRONG_VARIANT", "WRONG_QUANTITY", "OTHER"],
  EXPIRED: ["NEAR_EXPIRY", "ALREADY_EXPIRED"],
  QUALITY_ISSUE: ["TASTE_SMELL", "COLOR_FADED", "SIZE_MISMATCH", "TEXTURE_ISSUE", "OTHER"],
  CHANGED_MIND: ["NO_LONGER_NEEDED", "FOUND_BETTER_PRICE", "ORDER_BY_MISTAKE"],
};

function subReasonLabel(value: string): string {
  return i18n.t(`buyerApp:subReason.${value}`, value);
}

export function subReasonsFor(reason: ReturnReason | null | undefined): SubReasonOption[] {
  if (!reason) return [];
  return (SUB_REASON_VALUES_BY_REASON[reason] ?? []).map((value) => ({ value, labelMn: subReasonLabel(value) }));
}

export function subReasonLabelMn(reason: ReturnReason | null | undefined, value: string | null | undefined): string {
  if (!value) return "";
  return subReasonsFor(reason).find((o) => o.value === value)?.labelMn ?? value;
}
